import { useState, useEffect } from 'react';
import { Skeleton } from '../components/ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

// Hatwan API: { id, name, code, currency_code, order, sale, buy, created_at, updated_at }
export interface CurrencyRate {
  id?: number;
  name?: string;
  code?: string;
  currency_code?: string;
  order?: number;
  buy?: number;
  sale?: number;
  created_at?: string;
  updated_at?: string;
}

const REFRESH_COOLDOWN_SECONDS = 60;

export default function CurrencyExchange() {
  const [rates, setRates] = useState<CurrencyRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const fetchCurrencies = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/currencies') as {
        success?: boolean;
        data?: CurrencyRate[];
        cached?: boolean;
        lastFetched?: string;
      };
      if (!res.success || res.data == null) {
        setError((res as { error?: string }).error ?? 'Failed to load rates');
        setRates([]);
        return;
      }
      setCached(Boolean(res.cached));
      if (res.lastFetched) setLastFetched(res.lastFetched);
      setRates(Array.isArray(res.data) ? res.data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load currency rates');
      setRates([]);
    } finally {
      setLoading(false);
      setCooldownRemaining(REFRESH_COOLDOWN_SECONDS);
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  // Count down cooldown so refresh becomes available again
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const id = setInterval(() => {
      setCooldownRemaining((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownRemaining]);

  const getCode = (c: CurrencyRate) =>
    (c.currency_code ?? c.code ?? '').toString().toUpperCase();

  // No decimals for whole numbers; show decimals only when the value has them
  const formatRate = (n: number | undefined) => {
    if (n == null) return '—';
    const hasDecimals = Number(n) !== Math.floor(Number(n));
    return n.toLocaleString('en-US', {
      minimumFractionDigits: hasDecimals ? 1 : 0,
      maximumFractionDigits: 4,
    });
  };

  if (loading && rates.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">Loading rates…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
              Currency Exchange
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              USD, EUR, GBP · Hatwan Exchange
              {lastFetched && (
                <span className="ml-2 text-slate-400">
                  · {cached ? 'Cached' : 'Live'} {new Date(lastFetched).toLocaleString()}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchCurrencies}
            disabled={loading || cooldownRemaining > 0}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : cooldownRemaining > 0 ? `Refresh in ${cooldownRemaining}s` : 'Refresh'}
          </button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-800 text-sm mb-6">
            {error}
          </div>
        )}

        {rates.length === 0 && !error && (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-500">No currency data available.</p>
          </div>
        )}

        {rates.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {rates.map((c) => (
              <div
                key={getCode(c)}
                className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col"
              >
                <div className="px-5 pt-5 pb-1">
                  <div className="text-lg font-semibold text-slate-900">{getCode(c)}</div>
                  {c.name && (
                    <div className="text-sm text-slate-500 mt-0.5">{c.name}</div>
                  )}
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm text-slate-500">Buy</span>
                    <span className="text-lg font-semibold text-emerald-600 tabular-nums">
                      {formatRate(c.buy)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm text-slate-500">Sell</span>
                    <span className="text-lg font-semibold text-red-600 tabular-nums">
                      {formatRate(c.sale)}
                    </span>
                  </div>
                </div>
                {c.updated_at && (
                  <div className="mt-auto px-5 py-3 bg-slate-50 text-xs text-slate-400 border-t border-slate-100">
                    Updated {c.updated_at}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
